import React, { useState, useEffect, useCallback } from "react";
import {
  Stack, Typography, Paper, TextField, Button, Chip, Box, InputAdornment,
  CircularProgress, TablePagination, MenuItem, Select, IconButton,
  FormControlLabel, Switch,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRightOutlined";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import LaunchIcon from "@mui/icons-material/LaunchOutlined";
import { apiGet, apiList, apiPost, ApiError } from "../api/client";
import { parseImageUrls } from "../components/ImageGalleryCell";
import { usePersistedState } from "../api/persistedState";
import { useToast } from "../ui/Toaster";
import ShipLabelDialog from "../components/ShipLabelDialog";

// Color palette mirrors ShipLabelDialog's CARRIER_STYLE so the chip
// row on this page and the rate-list filter chips read identically.
const CARRIER_CHIPS = [
  { key: 'usps',  label: 'USPS',  color: 'info' },
  { key: 'fedex', label: 'FedEx', color: 'secondary' },
  { key: 'ups',   label: 'UPS',   color: 'warning' },
];

function useDebounced(v, ms = 300) {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
}

// Sort affordances for the page. The previous table-with-headers UX let
// the user click each column to sort; the card grid has no headers so
// this dropdown replaces that. The values stay aligned with the keys the
// backend's ``allowed_keys`` whitelist accepts.
const SORT_OPTIONS = [
  { label: "Location",            key: "location",      dir: "asc"  },
  // Ship-by-date asc bubbles the most-urgent (closest deadline) cards to
  // the top — the operator's actual triage order on a busy shipping day.
  { label: "Ship-by (urgent first)", key: "ship_by_date", dir: "asc"  },
  // Sale-date desc shows the newest orders first (default order-date sort).
  { label: "Order date (newest)",  key: "sale_date",     dir: "desc" },
  { label: "Order date (oldest)",  key: "sale_date",     dir: "asc"  },
  { label: "Title A→Z",            key: "cleaned_title", dir: "asc"  },
  { label: "Lot ID",               key: "lot_id",        dir: "asc"  },
  { label: "Qty (high→low)",       key: "order_qty",     dir: "desc" },
];

// Stable identifier for a row across the page (used for selection sets +
// pending-purchase tracking). Each unfulfilled row is uniquely a
// (order_id, line_item_id) pair; falling back to any other id shape when
// those aren't present.
function rowKey(row) {
  if (!row) return "";
  if (row.order_id && row.line_item_id) return `${row.order_id}::${row.line_item_id}`;
  return String(row.lot_id ?? row.item_id ?? row.id ?? "");
}

export default function UnfulfilledSales() {
  const { error: toastError, success: toastSuccess, info: toastInfo } = useToast();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const debounced = useDebounced(search, 300);
  const [sortIdx, setSortIdx] = useState(0);  // index into SORT_OPTIONS
  const sortKey = SORT_OPTIONS[sortIdx]?.key || "location";
  const sortDir = SORT_OPTIONS[sortIdx]?.dir || "asc";
  // Row currently being labeled. null = dialog closed.
  const [shipRow, setShipRow] = useState(null);
  // Multi-select for bulk shipping. Set of rowKey(row). When the operator
  // clicks "Ship selected", we run them through the ship dialog one at a
  // time — the first order's entered dims stick in the listing-dims
  // localStorage cache and pre-fill every subsequent card in the queue,
  // so bulk-similar packages take one dim entry instead of N.
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  // Queue of remaining rows to walk through after the current shipRow
  // dialog closes. Empty = no bulk session in progress.
  const [shipQueue, setShipQueue] = useState([]);
  // Dims captured from the first order in a bulk-ship session — passed
  // to every subsequent ShipLabelDialog invocation via ``sharedDims`` so
  // steps 1 is skipped and rates are auto-fetched. Cleared when the
  // queue empties (single-order flow after that).
  const [bulkDims, setBulkDims] = useState(null);
  // In-flight async purchases keyed by rowKey. Each entry is
  // ``{ row, error }`` — presence means "still pending"; a non-null
  // error means "purchase failed, card stays visible with the red
  // pill so the operator can retry". Cleared on success.
  const [pendingPurchases, setPendingPurchases] = useState(() => new Map());

  const toggleSelect = useCallback((row) => {
    const k = rowKey(row);
    if (!k) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedKeys(new Set()), []);

  // Kick off the first order in the bulk-ship queue. Once the dialog
  // closes (whether via successful async submit or a Cancel), the
  // useEffect below advances to the next queued row automatically.
  const startBulkShip = useCallback(() => {
    const queue = rows.filter((r) => selectedKeys.has(rowKey(r)));
    if (queue.length === 0) return;
    setShipQueue(queue.slice(1));
    setShipRow(queue[0]);
    // Deselect so the row-selection UI resets after the bulk session
    // starts; the visible in-flight state is now the "Buying label…"
    // chip on each card that's mid-purchase, not the checkbox.
    setSelectedKeys(new Set());
    // Fresh session — dims will be captured from the first order's
    // Get-rates click via onDimsCommitted.
    setBulkDims(null);
  }, [rows, selectedKeys]);

  // Advance the queue whenever the dialog is closed with more rows waiting.
  useEffect(() => {
    if (shipRow !== null) return;
    if (shipQueue.length === 0) return;
    const [next, ...rest] = shipQueue;
    setShipRow(next);
    setShipQueue(rest);
  }, [shipRow, shipQueue]);

  // Clear the shared bulk dims once the queue drains AND the last
  // dialog closes — subsequent single-order ship dialogs should use
  // per-listing pre-fill precedence, not the bulk operator's last-typed
  // values.
  useEffect(() => {
    if (shipRow === null && shipQueue.length === 0 && bulkDims != null) {
      setBulkDims(null);
    }
  }, [shipRow, shipQueue.length, bulkDims]);
  // Today's printed-label counter, per carrier. Resets at local
  // midnight server-side; we re-fetch after every successful purchase
  // so the chip row updates in step with the operator's flow.
  const [printedToday, setPrintedToday] = useState(
    { usps: 0, fedex: 0, ups: 0, other: 0, total: 0 }
  );
  const loadPrintedToday = useCallback(async () => {
    try {
      const data = await apiGet('/api/labels_printed_today');
      setPrintedToday({
        usps:  data?.usps  || 0,
        fedex: data?.fedex || 0,
        ups:   data?.ups   || 0,
        other: data?.other || 0,
        total: data?.total || 0,
      });
    } catch {
      // Silent — chip row just shows 0s if the endpoint hiccups.
    }
  }, []);
  useEffect(() => { loadPrintedToday(); }, [loadPrintedToday]);

  // "Show all orders" toggle. Default false — this page is primarily a
  // queue of work, so the unmarked default stays the queue view. When
  // on, the backend expands the eBay fetch to include FULFILLED orders
  // AND tells the OrderChecker fetcher to skip its needs-fulfillment
  // FIFO filter (already-shipped external rows show up too). Persisted
  // so the operator's last setting survives reloads.
  const [showAll, setShowAll] = usePersistedState("bidrl_unfulfilled_show_all", false);

  const load = useCallback(async ({ signal, force } = {}) => {
    setLoading(true);
    try {
      const params = {
        page, page_size: pageSize, q: debounced, sort: sortKey, order: sortDir,
      };
      // The backend caches eBay's fulfillment API response for 3 minutes
      // to stay under rate limits. Manual refresh button passes
      // force=1 to bypass that cache and re-hit eBay immediately.
      if (force) params.force = 1;
      if (showAll) params.all = 1;
      const res = await apiList("/api/unfullfilled_sales", params, { signal });
      setRows(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      if (e?.name === "AbortError") return;
      toastError("Couldn't load unfulfilled sales", e instanceof ApiError ? e.message : String(e));
      setRows([]); setTotal(0);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [page, pageSize, debounced, sortKey, sortDir, showAll, toastError]);

  useEffect(() => {
    const ctrl = new AbortController();
    load({ signal: ctrl.signal });
    return () => ctrl.abort();
  }, [load]);

  // Reset to page 1 whenever a filter changes — also covers showAll
  // so flipping the toggle doesn't strand the user on a now-empty page.
  useEffect(() => { setPage(1); }, [debounced, sortIdx, pageSize, showAll]);

  // Async purchase — called by ShipLabelDialog when it hands off the
  // payload instead of awaiting the POST itself. Fires the POST in the
  // background, keeps the card visible with a "Buying label…" pill,
  // then either removes the row on success or flips the pill to
  // "Purchase failed — retry" on failure.
  const handleAsyncSubmit = useCallback((payload) => {
    const rk = `${payload?.order_id ?? ""}::${payload?.line_item_id ?? ""}`;
    if (!rk || rk === "::") {
      toastError("Purchase couldn't start", "Missing order id");
      return;
    }
    // Snapshot the row so we can restore it if the purchase fails after
    // a background refresh has moved it out of ``rows``.
    const rowSnapshot = rows.find((r) => rowKey(r) === rk) || shipRow;
    setPendingPurchases((prev) => {
      const next = new Map(prev);
      next.set(rk, { row: rowSnapshot, error: null });
      return next;
    });

    (async () => {
      try {
        const data = await apiPost('/api/shipping_purchase', payload);
        // Success — take the card off the page + bump today's counter.
        setPendingPurchases((prev) => {
          const next = new Map(prev);
          next.delete(rk);
          return next;
        });
        setRows((prev) => prev.filter((r) => rowKey(r) !== rk));
        loadPrintedToday();
        toastSuccess(
          "Label purchased",
          `${data?.carrier_name || data?.carrier_code || ''} ${data?.tracking_number || ''}`.trim() || payload?.order_id
        );
        // Re-fetch in the background so any server-side state (label
        // suppression, external_label_log, etc.) is authoritative.
        load({ force: true });
      } catch (e) {
        const body = e instanceof ApiError ? (e.body || {}) : {};
        const msg = body.error || (e instanceof ApiError ? e.message : String(e));
        setPendingPurchases((prev) => {
          const next = new Map(prev);
          const cur = next.get(rk) || { row: rowSnapshot };
          next.set(rk, { row: cur.row, error: msg });
          return next;
        });
        // If the row already dropped out of ``rows`` (server-side
        // refresh raced), splice the snapshot back in so the operator
        // can retry from the visible card.
        setRows((prev) => (
          prev.some((r) => rowKey(r) === rk) || !rowSnapshot
            ? prev
            : [rowSnapshot, ...prev]
        ));
        toastError("Purchase failed", `${payload?.order_id}: ${msg}`);
      }
    })();
  }, [rows, shipRow, loadPrintedToday, load, toastError, toastSuccess]);

  return (
    <Box>
      {/* Page header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: '1.85rem', md: '2.5rem' } }}>
            Unfulfilled Sales
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {total.toLocaleString()} pending
          </Typography>
          {/* Today's printed-label counts per carrier. Resets at local
              midnight. Each chip shows count even when zero so the row
              shape is stable as the day progresses. */}
          <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`Today · ${printedToday.total}`}
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
            {CARRIER_CHIPS.map((c) => (
              <Chip
                key={c.key}
                size="small"
                label={`${c.label} ${printedToday[c.key]}`}
                color={c.color}
                variant={printedToday[c.key] > 0 ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600 }}
              />
            ))}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          {selectedKeys.size > 0 && (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={startBulkShip}
                startIcon={<LocalShippingOutlinedIcon />}
                sx={{ minWidth: { sm: 180 }, fontWeight: 600 }}
              >
                Ship selected ({selectedKeys.size})
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={clearSelection}
              >
                Clear
              </Button>
            </>
          )}
          <Button
            startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SyncIcon />}
            variant="contained"
            onClick={() => load({ force: true })}
            disabled={loading}
            sx={{ minWidth: { sm: 120 } }}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* Filters card */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap">
          <TextField
            placeholder="Search lot, title, UPC, notes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Select
            size="small"
            value={sortIdx}
            onChange={(e) => setSortIdx(Number(e.target.value))}
            sx={{ minWidth: 160 }}
          >
            {SORT_OPTIONS.map((opt, i) => (
              <MenuItem key={opt.key + opt.dir} value={i}>
                Sort: {opt.label}
              </MenuItem>
            ))}
          </Select>
          {/* Show-all toggle. Default off — when on, the backend pulls
              FULFILLED orders from eBay AND skips OrderChecker's
              needs-fulfillment FIFO filter, so already-shipped orders
              from both sources appear alongside the open ones. State
              is persisted via localStorage so the operator's last
              setting survives a reload. */}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={!!showAll}
                onChange={(e) => setShowAll(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: 13 }}>
                Show all orders
              </Typography>
            }
            sx={{ m: 0 }}
          />
        </Stack>
      </Paper>

      {/* Card grid — phone gets 1 column (no horizontal scroll), tablet 2,
          desktop 3. Each card shows only what's needed to pick the item
          off the shelf: picture, title, lot id, location, and the order
          qty when >1. UPC + notes from the old table are dropped from
          the card face since the operator confirmed they don't need
          them while physically pulling stock. */}
      {loading && rows.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
          <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
            <CircularProgress size={14} />
            <span>Loading…</span>
          </Stack>
        </Paper>
      ) : !loading && rows.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 6 }}>
          <Stack alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
            <WarningAmberOutlinedIcon fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Nothing unfulfilled</Typography>
            <Typography variant="caption">
              All open orders have inventory mapped.
            </Typography>
          </Stack>
        </Paper>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 1.5,
          position: 'relative',
        }}>
          {rows.map((row) => {
            const rk = rowKey(row);
            const pend = pendingPurchases.get(rk) || null;
            return (
              <UnfulfilledCard
                key={rk || row.lot_id || `${row.order_id}-${row.line_item_id}`}
                row={row}
                onBuyLabel={() => setShipRow(row)}
                selected={selectedKeys.has(rk)}
                onToggleSelect={() => toggleSelect(row)}
                pending={!!pend && !pend.error}
                pendingError={pend?.error || null}
              />
            );
          })}
          {loading && rows.length > 0 && (
            <Box sx={{
              position: 'absolute', top: -28, right: 0,
              display: 'inline-flex', alignItems: 'center', gap: 0.75,
              bgcolor: 'background.paper', border: 1, borderColor: 'divider',
              borderRadius: 1, px: 1, py: 0.25, fontSize: '0.7rem',
              color: 'text.secondary',
            }}>
              <CircularProgress size={12} /> Refreshing
            </Box>
          )}
        </Box>
      )}

      {/* Pager — sits below the grid; the rowsPerPage menu lets the user
          load a longer page when they're picking a whole batch at once. */}
      <Paper variant="outlined" sx={{ mt: 2 }}>
        <TablePagination
          component="div"
          count={total}
          page={Math.max(0, page - 1)}
          onPageChange={(_, p) => setPage(p + 1)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Paper>

      {/* Label-purchase modal. Mounted at the page level so it survives
          the card-grid re-render after a successful purchase. */}
      <ShipLabelDialog
        open={!!shipRow}
        row={shipRow}
        onClose={() => setShipRow(null)}
        // Async submit — dialog closes immediately after Buy, POST fires
        // in the background, and we keep the card visible with an
        // in-flight pill until the purchase resolves. Any orders
        // queued behind this one auto-open as soon as the dialog closes.
        onSubmitAsync={handleAsyncSubmit}
        queueRemaining={shipQueue.length}
        // Bulk-ship — after the operator commits dimensions once (via
        // the first order's Get-rates click), reuse those same values
        // for every subsequent order in the queue. Step 1 is skipped
        // for orders 2+; the operator can still tap "Edit dimensions"
        // on the rate screen for any order that needs a different box.
        sharedDims={shipQueue.length > 0 || bulkDims != null ? bulkDims : null}
        onDimsCommitted={(dims) => {
          // Update the shared set on every Get-rates click. That way
          // if the operator uses "Edit dimensions" partway through the
          // queue, the correction propagates to the remaining orders.
          setBulkDims(dims);
        }}
        onPurchased={() => {
          // Sync path — used only if a caller ever removes onSubmitAsync
          // for a specific row. Currently unreached because we always
          // pass onSubmitAsync above.
          load({ force: true });
          loadPrintedToday();
        }}
      />
    </Box>
  );
}

// Tile: phone-friendly card with carousel up top, title + lot id +
// location underneath. The lot id is the loud element — it's the
// physical-label identifier the operator scans for in a bin, so we
// give it weight 700 and bump the size well above the title's. The
// carousel cycles through every image the listing has so the user can
// confirm which exact variant they're picking from a shelf full of
// similar items.
function UnfulfilledCard({ row, onBuyLabel, selected, onToggleSelect, pending, pendingError }) {
  const images = parseImageUrls(row.image_urls);
  const [index, setIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const qty = Number(row.order_qty || 1);
  const title = row.cleaned_title || row.title || "—";
  const location = row.location;
  const total = images.length;
  // Date badge — show eBay's ship-by deadline when present (the
  // operator's actual carrier-handoff clock), else fall back to the
  // sale's creation date so the operator at least sees how stale the
  // order is. Color codes: red = overdue, amber = ≤24h left, default
  // gray = informational.
  const dateInfo = (() => {
    // eBay returns ship_by_date as an ISO timestamp at end-of-business-day
    // Pacific (eBay HQ timezone). Rendering with the operator's local
    // timezone rolls the calendar day forward — e.g. a deadline at
    // ``2026-07-01T06:59:59Z`` (= 11:59 PM PDT June 30) shows up as
    // "Jul 1" in Eastern time even though eBay Seller Hub displays it
    // as "Jun 30". Force the display to Pacific so the calendar day
    // matches the deadline eBay actually enforces.
    const EBAY_TZ = 'America/Los_Angeles';
    const shipBy = row.ship_by_date;
    if (shipBy) {
      const d = new Date(shipBy);
      if (!Number.isNaN(d.getTime())) {
        const hoursLeft = (d.getTime() - Date.now()) / 3_600_000;
        return {
          label: `Ship by ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: EBAY_TZ })}`,
          color: hoursLeft < 0 ? 'error' : hoursLeft < 24 ? 'warning' : 'default',
          variant: hoursLeft < 24 ? 'filled' : 'outlined',
        };
      }
    }
    const saleDate = row.sale_date;
    if (saleDate) {
      const d = new Date(saleDate);
      if (!Number.isNaN(d.getTime())) {
        // Sale-date uses creationDate / paymentDate, which is also a UTC
        // timestamp at the moment the buyer paid. Render in the operator's
        // local time — "Sold Jun 29" should reflect when the operator's
        // day actually showed the sale arrive, not eBay HQ's.
        return {
          label: `Sold ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`,
          color: 'default',
          variant: 'outlined',
        };
      }
    }
    return null;
  })();
  // External rows come from OrderChecker's cache — no BidRL lot or
  // shelf location to display, so we re-skin the card with an "External"
  // chip and use the buyer name as the loud identifier.
  const isExternal = row.source === "external";
  // Personal rows are hardcoded reminders for shipments handled outside
  // eBay/OrderChecker. Same layout as external but the label CTA is
  // hidden (no eBay label to buy) and the chip reads "Personal".
  const isPersonal = row.source === "personal";
  // Preload neighboring frames so a flick through the carousel doesn't
  // blink white on slow connections. Cheap; runs at most 2 fetches per
  // navigation.
  useEffect(() => {
    if (total <= 1) return;
    const preload = (i) => {
      const img = new Image();
      img.src = images[((i % total) + total) % total];
    };
    preload(index + 1);
    preload(index - 1);
  }, [index, images, total]);

  const goPrev = (e) => {
    e?.stopPropagation?.();
    setIndex((i) => ((i - 1) % total + total) % total);
  };
  const goNext = (e) => {
    e?.stopPropagation?.();
    setIndex((i) => (i + 1) % total);
  };
  const current = total > 0 ? images[index] : null;

  // Border color reflects state precedence:
  //   1. failed pending purchase  → red (retry needed)
  //   2. in-flight pending purchase → pulsing amber
  //   3. selected for bulk ship   → thick primary
  //   4. external row             → info blue
  //   5. missing shelf location   → warning
  //   6. otherwise                → divider
  let borderColor = 'divider';
  let borderWidth = 1;
  if (pendingError) { borderColor = 'error.main'; borderWidth = 2; }
  else if (pending)      { borderColor = 'warning.main'; borderWidth = 2; }
  else if (selected)     { borderColor = 'primary.main'; borderWidth = 2; }
  else if (isPersonal)   { borderColor = 'secondary.main'; borderWidth = 2; }
  else if (isExternal)   { borderColor = 'info.light'; borderWidth = 2; }
  else if (!location)    { borderColor = 'warning.light'; borderWidth = 1; }

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        borderColor,
        borderWidth,
        position: 'relative',
      }}
    >
      {/* Multi-select checkbox — top-left corner overlay on the image.
          The operator can queue up several orders that share the same
          box dimensions, click Ship selected in the toolbar, and the
          dims they enter on the first order pre-fill every subsequent
          card in the queue automatically (via the localStorage
          listing-dims cache). */}
      {typeof onToggleSelect === 'function' && !isPersonal && (
        <Box
          onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
          sx={{
            position: 'absolute', top: 6, left: 6, zIndex: 3,
            bgcolor: selected ? 'primary.main' : 'rgba(255,255,255,0.9)',
            color: selected ? 'primary.contrastText' : 'text.primary',
            border: 1, borderColor: selected ? 'primary.main' : 'divider',
            borderRadius: 1, width: 28, height: 28,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: 16, lineHeight: 1, userSelect: 'none',
            boxShadow: selected ? 1 : 0,
          }}
          aria-label={selected ? "Deselect for bulk ship" : "Select for bulk ship"}
        >
          {selected ? '✓' : ''}
        </Box>
      )}
      {/* Pending / failed purchase pill — top-right corner overlay. */}
      {(pending || pendingError) && (
        <Chip
          size="small"
          label={pendingError ? 'Purchase failed — retry' : 'Buying label…'}
          color={pendingError ? 'error' : 'warning'}
          icon={pendingError ? <WarningAmberOutlinedIcon fontSize="small" /> : <CircularProgress size={10} sx={{ color: 'inherit' }} />}
          sx={{ position: 'absolute', top: 6, right: 6, zIndex: 3, fontWeight: 600 }}
        />
      )}
      {/* Carousel area — fills card width on mobile so the operator
          can identify the item from across the room. aspectRatio keeps
          the row heights consistent across the grid. */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1 / 1',
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {current ? (
          <img
            src={current}
            alt={`${title} ${index + 1}/${total}`}
            loading="lazy"
            onClick={() => setZoomed(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
          />
        ) : (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            No image
          </Typography>
        )}

        {/* Prev/next arrows — only render when there's more than one
            frame so the chrome stays out of the way on single-image
            listings. Sized generously for thumb taps on a phone. */}
        {total > 1 && (
          <>
            <IconButton
              size="small"
              onClick={goPrev}
              aria-label="Previous image"
              sx={{
                position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.85)',
                '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                width: 32, height: 32,
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={goNext}
              aria-label="Next image"
              sx={{
                position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.85)',
                '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                width: 32, height: 32,
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>

            {/* Frame counter pill, bottom-center. More reliable than a
                row of dots when there are 8-10 images. */}
            <Box
              sx={{
                position: 'absolute', bottom: 6, left: '50%',
                transform: 'translateX(-50%)',
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                px: 0.9, py: 0.15,
                borderRadius: 1,
                fontSize: 11, fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {index + 1}/{total}
            </Box>
          </>
        )}

        {qty > 1 && (
          <Chip
            size="small"
            label={`×${qty}`}
            color="warning"
            sx={{
              position: 'absolute', top: 6, right: 6,
              fontWeight: 700, height: 22, fontSize: 12,
            }}
          />
        )}
      </Box>

      {/* Body: lot id (loud), title (clamped to 2 lines), location chip.
          Reordered so the lot id is the first thing the eye lands on
          when the card scrolls into view. External rows have no BidRL
          lot — we show the buyer's name there instead so the operator
          still has a per-card identifier. */}
      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1 }}>
        <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
          <Typography
            sx={{
              fontFamily: 'monospace',
              fontWeight: 700,
              fontSize: '1.05rem',
              lineHeight: 1.2,
              color: 'text.primary',
              letterSpacing: 0.3,
              wordBreak: 'break-all',
            }}
          >
              {(isExternal || isPersonal)
              ? (row.ship_to?.full_name || (isPersonal ? "Personal" : "External"))
              : (row.lot_id || "—")}
          </Typography>
          {isPersonal ? (
            <Chip
              size="small"
              label="Personal"
              color="secondary"
              sx={{ fontWeight: 700, height: 26, fontSize: 13, ml: 'auto' }}
            />
          ) : isExternal ? (
            <Chip
              size="small"
              label="External"
              color="info"
              sx={{ fontWeight: 700, height: 26, fontSize: 13, ml: 'auto' }}
            />
          ) : location ? (
            <Chip
              size="small"
              label={location}
              color="primary"
              sx={{ fontWeight: 700, height: 26, fontSize: 13, ml: 'auto' }}
            />
          ) : (
            <Chip
              size="small"
              label="Missing"
              color="warning"
              sx={{ fontWeight: 700, height: 26, fontSize: 13, ml: 'auto' }}
            />
          )}
        </Stack>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}
        >
          {title}
        </Typography>

        {(dateInfo || Number(row.sold_price) > 0) && (
          <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ alignItems: 'center' }}>
            {dateInfo && (
              <Chip
                size="small"
                label={dateInfo.label}
                color={dateInfo.color}
                variant={dateInfo.variant}
                sx={{
                  fontWeight: dateInfo.variant === 'filled' ? 700 : 500,
                  height: 22,
                  fontSize: 12,
                }}
              />
            )}
            {Number(row.sold_price) > 0 && (
              <Chip
                size="small"
                label={`$${Number(row.sold_price).toFixed(2)}`}
                color="success"
                variant="outlined"
                sx={{ fontWeight: 600, height: 22, fontSize: 12 }}
              />
            )}
          </Stack>
        )}

        {/* Bottom-of-card actions. Buy-label is the primary CTA (widest
            slot). "View" opens the eBay Seller Hub order-details page in
            a new tab — one click to pivot into eBay's UI for shipping
            notes, buyer messages, or issuing a refund. Personal rows
            aren't real eBay orders, so they show a static "ship manually"
            note instead. */}
        {isPersonal ? (
          <Box
            sx={{
              mt: 'auto', px: 1, py: 0.75, borderRadius: 1,
              bgcolor: 'action.hover', color: 'text.secondary',
              fontSize: 12, textAlign: 'center', fontWeight: 500,
            }}
          >
            Ship manually — personal order
          </Box>
        ) : (
          <Stack direction="row" gap={0.75} sx={{ mt: 'auto' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LaunchIcon />}
              component="a"
              href={row.order_id
                ? `https://www.ebay.com/mesh/ord/details?orderid=${encodeURIComponent(String(row.order_id).trim())}`
                : undefined}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              disabled={!row.order_id}
              sx={{ flexShrink: 0 }}
            >
              View
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<LocalShippingOutlinedIcon />}
              onClick={onBuyLabel}
              disabled={!onBuyLabel || !row.order_id}
              sx={{ flex: 1 }}
            >
              Buy label
            </Button>
          </Stack>
        )}
      </Box>

      {/* Full-screen image preview with its own carousel — useful when
          the shelf has multiple lookalike items and the operator needs
          to compare against every product photo at full resolution.
          Backdrop tap closes; the X button is there for keyboard /
          accessibility paths. */}
      {zoomed && current && (
        <FullScreenCarousel
          images={images}
          index={index}
          setIndex={setIndex}
          onClose={() => setZoomed(false)}
          alt={title}
        />
      )}
    </Paper>
  );
}

// Full-screen image viewer with arrow + keyboard navigation. Pulled
// out of UnfulfilledCard so the JSX above stays readable. Owned by
// the parent's index/setIndex so the card and the modal stay in sync
// (closing the modal leaves the card on whatever image you last
// stopped on).
function FullScreenCarousel({ images, index, setIndex, onClose, alt }) {
  const total = images.length;
  const goPrev = (e) => {
    e?.stopPropagation?.();
    setIndex((i) => ((i - 1) % total + total) % total);
  };
  const goNext = (e) => {
    e?.stopPropagation?.();
    setIndex((i) => (i + 1) % total);
  };
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  // Lightweight swipe handling — track the touch's horizontal delta on
  // release. Anything past 50px triggers a frame change; under that and
  // we treat the tap as a dismissal. Vertical drags don't fire either
  // arrow so the user can still scroll past the modal if needed.
  const [touchStart, setTouchStart] = useState(null);
  const onTouchStart = (e) => {
    const t = e.touches[0];
    setTouchStart({ x: t.clientX, y: t.clientY });
  };
  const onTouchEnd = (e) => {
    if (!touchStart) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.x;
    const dy = Math.abs(t.clientY - touchStart.y);
    setTouchStart(null);
    if (Math.abs(dx) < 50 || dy > 80) return;
    if (dx > 0) goPrev();
    else goNext();
  };

  return (
    <Box
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      sx={{
        position: 'fixed', inset: 0,
        bgcolor: 'rgba(0,0,0,0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1300,
        p: 2,
        touchAction: 'pan-y',
      }}
    >
      <img
        src={images[index]}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
      />

      {total > 1 && (
        <>
          <IconButton
            onClick={goPrev}
            aria-label="Previous image"
            sx={{
              position: 'fixed', left: 8, top: '50%', transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              width: 48, height: 48,
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            onClick={goNext}
            aria-label="Next image"
            sx={{
              position: 'fixed', right: 8, top: '50%', transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              width: 48, height: 48,
            }}
          >
            <ChevronRightIcon />
          </IconButton>

          <Box
            sx={{
              position: 'fixed', bottom: 16, left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              px: 1.5, py: 0.5,
              borderRadius: 1,
              fontSize: 13, fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {index + 1}/{total}
          </Box>
        </>
      )}

      <IconButton
        onClick={onClose}
        aria-label="Close"
        sx={{
          position: 'fixed', top: 8, right: 8,
          bgcolor: 'rgba(255,255,255,0.15)',
          color: 'white',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
        }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );
}
